import { Input, Popover } from "@mantine/core";
import { Link, RichTextEditor, type RichTextEditorProps } from "@mantine/tiptap";
import {
    Scope,
    useFieldInput,
    type FieldActions,
    type FieldInputConvertFn,
    type FieldInputParseFn,
    type ValidationError,
    type ValidationErrorDisplayMode
} from "@ngt-form/core";
import { getInputClassName, getInputError } from "@ngt-form/mantine";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";

import { MergeField, type MergeFieldOption } from "./MergeField";
import { createMergeFieldSuggestion, MergeFieldSuggestionList } from "./MergeFieldSuggestionList";
import classes from "./RichTextEditorInput.module.css";

type EditorFieldActions<
    TFormValue extends object,
    TValidationError extends ValidationError,
    TFormMetadata extends object,
    TFieldValue,
    TLookupItem
> = FieldActions<TFormValue, TValidationError, TFormMetadata, TFieldValue, TLookupItem>;

export type RichTextEditorInputProps<
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
> = Partial<
    Omit<RichTextEditorProps, "children" | "editor" | "onChange" | "onFocus" | "onBlur">
> & {
    label?: ReactNode;
    hideLabel?: boolean | null;
    disabled?: boolean | null;
    minHeight?: CSSProperties["minHeight"];
    mergeFields?: readonly MergeFieldOption[] | null;
    parse?: FieldInputParseFn<TFieldValue, string | undefined> | null;
    convert?: FieldInputConvertFn<TFieldValue, string | undefined> | null;
    validationErrorDisplayMode?: ValidationErrorDisplayMode | null;
    onChange?: (
        html: string,
        value: TFieldValue | null | undefined,
        fieldActions: EditorFieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
    onFocus?: (
        fieldActions: EditorFieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
    onBlur?: (
        fieldActions: EditorFieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
};

/**
 * Treats missing HTML and the exact empty paragraph as empty without trimming other markup.
 */
const normaliseHtml = (html: string | null | undefined) =>
    !html || html === "<p></p>" ? "" : html;

const richTextTagPattern =
    /<\/?(?:p|br|strong|em|u|s|a|span|mark|sup|sub|h1|h2|h3|h4|h5|h6|ul|ol|li|blockquote|pre|code|hr)(?:\s[^>]*)?\s*\/?>/i;

const textColors = [
    "#25262b",
    "#868e96",
    "#fa5252",
    "#e64980",
    "#be4bdb",
    "#7950f2",
    "#4c6ef5",
    "#228be6",
    "#15aabf",
    "#12b886",
    "#40c057",
    "#82c91e",
    "#fab005",
    "#fd7e14"
];

export const richTextExtensions = [
    StarterKit.configure({ link: false }),
    Link,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    TextStyle,
    Color,
    Superscript,
    Subscript,
    Highlight
];

/**
 * Converts plain text into escaped paragraph HTML while preserving recognized rich-text input.
 * Postconditions: Empty or recognized-tag content is returned unchanged. Other input is escaped
 * before newline-to-br conversion. This helper is not the display sanitization boundary.
 */
const toEditorContent = (value: string) => {
    if (!value || richTextTagPattern.test(value)) {
        return value;
    }

    const encodedValue = value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");

    return `<p>${encodedValue.replace(/\r?\n/g, "<br>")}</p>`;
};

/**
 * Binds the rich-text editor to form state, validation and optional merge-field controls.
 * Postconditions: Editor changes parse then update form flags before notifying the caller. External
 * content synchronization suppresses update events. Focus and blur set focused/touched flags before
 * callbacks. Disabled state hides the toolbar and is synchronized into editor editability.
 * Assumptions: Form/editor contexts are valid; refs expose latest callbacks and merge choices without
 * recreating the configured extension. Parsing and caller failures propagate; no persistence occurs.
 */
export const RichTextEditorInput = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    disabled: componentDisabled,
    label: componentLabel,
    hideLabel,
    minHeight,
    mergeFields,
    parse,
    convert,
    validationErrorDisplayMode: componentValidationErrorDisplayMode,
    className: componentClassName,
    style: componentStyle,
    onChange,
    onFocus,
    onBlur,
    ...props
}: RichTextEditorInputProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const {
        actions,
        snapshot,
        parseValue,
        convertValue,
        validationResult,
        validationErrors,
        validationErrorTypeName,
        disabled,
        label
    } = useFieldInput<
        TFormValue,
        TValidationError,
        TFormMetadata,
        TFieldValue,
        TLookupItem,
        string | undefined
    >({
        scope: Scope.Context,
        label: componentLabel,
        parse,
        convert,
        disabled: componentDisabled,
        validationErrorDisplayMode: componentValidationErrorDisplayMode,
        subscription: {
            value: true,
            label: true,
            lookup: false,
            initialValue: false,
            dirty: false,
            focused: false,
            touched: false
        }
    });

    const html = normaliseHtml(convertValue(snapshot.value));
    const editorContent = toEditorContent(html);
    const [mergeFieldMenuOpened, setMergeFieldMenuOpened] = useState(false);
    const mergeFieldsRef = useRef(mergeFields ?? []);
    mergeFieldsRef.current = mergeFields ?? [];
    const mergeFieldExtension = useMemo(
        () =>
            MergeField.configure({
                suggestion: createMergeFieldSuggestion(() => mergeFieldsRef.current)
            }),
        []
    );
    const inputState = useRef({
        actions,
        parseValue,
        onChange,
        onFocus,
        onBlur,
        editorContent
    });
    // Editor callbacks read current form actions and handlers without replacing the editor.
    inputState.current = {
        actions,
        parseValue,
        onChange,
        onFocus,
        onBlur,
        editorContent
    };

    const editor = useEditor({
        extensions: [...richTextExtensions, mergeFieldExtension],
        content: editorContent,
        editable: !disabled,
        /**
         * Ignores equivalent normalized content; otherwise parses and updates the field before onChange.
         */
        onUpdate: ({ editor: updatedEditor }) => {
            const nextHtml = updatedEditor.isEmpty ? "" : updatedEditor.getHTML();
            const state = inputState.current;

            if (normaliseHtml(nextHtml) === normaliseHtml(state.editorContent)) {
                return;
            }

            const value = state.parseValue(nextHtml);

            state.actions.setValue(value, {
                notify: true,
                updateDirty: true,
                updateFocused: true,
                updateTouched: true
            });
            state.onChange?.(nextHtml, value, state.actions);
        },
        /**
         * Marks the field focused without touching it before invoking the caller.
         */
        onFocus: () => {
            const state = inputState.current;
            state.actions.setFocused(true, { notify: true, updateTouched: false });
            state.onFocus?.(state.actions);
        },
        /**
         * Clears focus and marks touched before invoking the caller.
         */
        onBlur: () => {
            const state = inputState.current;
            state.actions.setFocused(false, { notify: true, updateTouched: true });
            state.onBlur?.(state.actions);
        }
    });

    useEffect(() => {
        editor?.setEditable(!disabled, false);
    }, [disabled, editor]);

    useEffect(() => {
        if (!editor || normaliseHtml(editor.getHTML()) === editorContent) {
            return;
        }

        // Avoid feeding externally supplied content back through the editor update callback.
        editor.commands.setContent(editorContent, { emitUpdate: false });
    }, [editor, editorContent]);

    const className = useMemo(
        () => getInputClassName(componentClassName, validationErrorTypeName, classes),
        [componentClassName, validationErrorTypeName]
    );
    const error = getInputError(validationErrors, !!validationResult);
    return (
        <Input.Wrapper
            label={hideLabel ? undefined : label}
            error={error}
        >
            <RichTextEditor
                {...props}
                editor={editor}
                className={className}
                data-error={error ? true : undefined}
                aria-invalid={error ? true : undefined}
                style={componentStyle}
            >
                {!disabled && (
                    <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.H1 />
                            <RichTextEditor.H2 />
                            <RichTextEditor.H3 />
                            <RichTextEditor.H4 />
                            <RichTextEditor.H5 />
                            <RichTextEditor.H6 />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                            <RichTextEditor.Strikethrough />
                            <RichTextEditor.Superscript />
                            <RichTextEditor.Subscript />
                            <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                            <RichTextEditor.Blockquote />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.AlignLeft />
                            <RichTextEditor.AlignCenter />
                            <RichTextEditor.AlignRight />
                            <RichTextEditor.AlignJustify />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Code />
                            <RichTextEditor.CodeBlock />
                            <RichTextEditor.Highlight />
                            <RichTextEditor.Hr />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.ColorPicker colors={textColors} />
                            <RichTextEditor.UnsetColor />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Link />
                            <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Undo />
                            <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>

                        {!!mergeFields?.length && (
                            <RichTextEditor.ControlsGroup>
                                <Popover
                                    onChange={setMergeFieldMenuOpened}
                                    opened={mergeFieldMenuOpened}
                                    position="bottom-start"
                                    withinPortal={false}
                                >
                                    <Popover.Target>
                                        <RichTextEditor.Control
                                            aria-expanded={mergeFieldMenuOpened}
                                            aria-label="Insert merge field"
                                            onClick={() =>
                                                setMergeFieldMenuOpened(opened => !opened)
                                            }
                                            title="Insert merge field"
                                        >
                                            <Icon icon={AppSettings.icons.mergeField} />
                                        </RichTextEditor.Control>
                                    </Popover.Target>
                                    <Popover.Dropdown p="xs">
                                        <MergeFieldSuggestionList
                                            command={field => {
                                                editor
                                                    ?.chain()
                                                    .focus()
                                                    .insertMergeField(field)
                                                    .run();
                                                setMergeFieldMenuOpened(false);
                                            }}
                                            focusOnMount
                                            items={mergeFields}
                                            onClose={() => setMergeFieldMenuOpened(false)}
                                        />
                                    </Popover.Dropdown>
                                </Popover>
                            </RichTextEditor.ControlsGroup>
                        )}
                    </RichTextEditor.Toolbar>
                )}

                <RichTextEditor.Content
                    className={classes.content}
                    style={{ minHeight }}
                />
            </RichTextEditor>
        </Input.Wrapper>
    );
};

export default RichTextEditorInput;
